import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  isTrackReference,
  useConnectionState,
  useLocalParticipant,
  useTracks,
  type TrackReference,
} from '@livekit/components-react';
import { ConnectionState, ParticipantEvent, Track } from 'livekit-client';

import { OralExamSupervisorStatus } from '@/common/api/models/oralExamSupervisorStatus';

export type SupervisionPublishState = {
  /** Browser blocked camera while Appear/Engage rung is active for this booking. */
  cameraBlocked: boolean;
  retryCamera: () => Promise<void>;
  /** Local lecturer camera for tile self-preview when publishing succeeds. */
  selfPreviewTrack: TrackReference | null;
};

function shouldPublishSupervisionCamera(
  supervisorStatus: OralExamSupervisorStatus,
  observePublish: boolean,
): boolean {
  return observePublish
    || supervisorStatus === OralExamSupervisorStatus.here_with_you
    || supervisorStatus === OralExamSupervisorStatus.engaged;
}

/**
 * Rung-gated camera publish for the lecturer's subscribe-only tile connection.
 * Reads persisted {@link OralExamSupervisorStatus} — never a client-only appear flag.
 */
export function SupervisionPublishGuard({
  supervisorStatus,
  observePublish = false,
  children,
}: {
  supervisorStatus: OralExamSupervisorStatus;
  /** Private look-in: publish this lecturer's camera into the student's face. */
  observePublish?: boolean;
  children: (state: SupervisionPublishState) => React.ReactNode;
}) {
  const { localParticipant } = useLocalParticipant();
  const connectionState = useConnectionState();
  const roomConnected = connectionState === ConnectionState.Connected;
  const publishWanted = shouldPublishSupervisionCamera(supervisorStatus, observePublish);
  const [cameraBlocked, setCameraBlocked] = useState(false);
  const hadLiveCameraRef = useRef(false);

  const localCameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: false }],
  ) as TrackReference[];

  const selfPreviewTrack = useMemo(() => {
    if (!publishWanted || cameraBlocked) return null;
    const localTrack = localCameraTracks.find((trackRef) => trackRef.participant.isLocal);
    if (!localTrack || !isTrackReference(localTrack)) return null;
    if (!localTrack.publication.track || localTrack.publication.isMuted) return null;
    return localTrack;
  }, [cameraBlocked, localCameraTracks, publishWanted]);

  const syncBlockedFromPublication = useCallback(() => {
    if (!publishWanted) return;
    const publication = localParticipant.getTrackPublication(Track.Source.Camera);
    const live = Boolean(publication?.track) && !publication?.isMuted;
    if (live) {
      hadLiveCameraRef.current = true;
      setCameraBlocked(false);
      return;
    }
    if (hadLiveCameraRef.current) {
      setCameraBlocked(true);
    }
  }, [localParticipant, publishWanted]);

  const enableCamera = useCallback(async (): Promise<boolean> => {
    try {
      await localParticipant.setCameraEnabled(true);
      hadLiveCameraRef.current = true;
      setCameraBlocked(false);
      return true;
    } catch {
      setCameraBlocked(true);
      return false;
    }
  }, [localParticipant]);

  const retryCamera = useCallback(async () => {
    await enableCamera();
  }, [enableCamera]);

  useEffect(() => {
    if (!publishWanted) {
      hadLiveCameraRef.current = false;
      setCameraBlocked(false);
      localParticipant.setCameraEnabled(false).catch(() => {});
      return undefined;
    }

    if (!roomConnected) {
      return undefined;
    }

    enableCamera();

    const onTrackLifecycle = () => {
      syncBlockedFromPublication();
    };

    localParticipant.on(ParticipantEvent.LocalTrackPublished, onTrackLifecycle);
    localParticipant.on(ParticipantEvent.LocalTrackUnpublished, onTrackLifecycle);
    localParticipant.on(ParticipantEvent.TrackMuted, onTrackLifecycle);
    localParticipant.on(ParticipantEvent.TrackUnpublished, onTrackLifecycle);

    return () => {
      localParticipant.off(ParticipantEvent.LocalTrackPublished, onTrackLifecycle);
      localParticipant.off(ParticipantEvent.LocalTrackUnpublished, onTrackLifecycle);
      localParticipant.off(ParticipantEvent.TrackMuted, onTrackLifecycle);
      localParticipant.off(ParticipantEvent.TrackUnpublished, onTrackLifecycle);
    };
  }, [
    enableCamera,
    localParticipant,
    publishWanted,
    roomConnected,
    syncBlockedFromPublication,
  ]);

  return (
    <>
      {children({
        cameraBlocked: publishWanted && cameraBlocked,
        retryCamera,
        selfPreviewTrack,
      })}
    </>
  );
}
